<template>
    <Teleport to="body">
        <Transition name="fade">
            <div v-if="isRateLimited"
                class="fixed inset-0 z-[9998] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">

                <!-- Outer Container -->
                <div
                    class="bg-[#151517] border border-white/10 rounded-2xl shadow-2xl w-full max-w-sm flex flex-col overflow-hidden">

                    <!-- Header -->
                    <div
                        class="px-6 py-5 border-b border-white/5 bg-[#111214]/95 backdrop-blur-xl flex items-center gap-4 z-20">
                        <div class="p-2.5 bg-red-500/10 rounded-xl text-red-500 border border-red-500/20">
                            <ShieldAlertIcon class="h-6 w-6" />
                        </div>
                        <div>
                            <h3 class="text-xl font-bold text-white tracking-tight">Too Many Requests</h3>
                        </div>
                    </div>

                    <!-- Body -->
                    <div class="p-6 relative z-10 flex flex-col gap-5">
                        <p class="text-gray-400 text-sm leading-relaxed">
                            You've been rate-limited by our servers to protect the system. Please wait before
                            interacting again.
                        </p>

                        <!-- Timer Highlight Box -->
                        <div
                            class="bg-black/20 border border-white/5 rounded-xl p-5 flex flex-col items-center justify-center">
                            <div class="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                                Time Remaining
                            </div>
                            <div class="text-4xl font-mono font-bold text-red-400 tracking-wider">
                                {{ formattedTime }}
                            </div>
                        </div>
                    </div>

                    <!-- Footer -->
                    <div
                        class="px-6 py-4 bg-[#111214]/95 backdrop-blur-xl border-t border-white/5 flex justify-center items-center z-20">
                        <p class="text-xs text-gray-500 flex items-center gap-2">
                            <Loader2Icon class="h-3.5 w-3.5 animate-spin text-gray-400" />
                            Automatically resuming when timer expires...
                        </p>
                    </div>

                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, onUnmounted, computed } from 'vue';
import { storeToRefs } from 'pinia';
import {
    ShieldAlert as ShieldAlertIcon,
    Loader2 as Loader2Icon
} from 'lucide-vue-next';
import { useRateLimitStore } from '@/stores/rateLimit';

const store = useRateLimitStore();
const { isRateLimited, resetTimestamp } = storeToRefs(store);

const timeLeft = ref(0);
let intervalId: ReturnType<typeof setInterval> | null = null;

// Format seconds into MM:SS
const formattedTime = computed(() => {
    const m = Math.floor(timeLeft.value / 60).toString().padStart(2, '0');
    const s = (timeLeft.value % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
});

const updateTimer = () => {
    const remainingSeconds = Math.ceil((resetTimestamp.value - Date.now()) / 1000);

    if (remainingSeconds <= 0) {
        store.clearRateLimit();
        timeLeft.value = 0;
        clearTimer();
    } else {
        timeLeft.value = remainingSeconds;
    }
};

const clearTimer = () => {
    if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
    }
};

// Reactively start/stop the timer when rate limit status changes
watch(isRateLimited, (newValue) => {
    if (newValue) {
        updateTimer(); // Initial update immediately
        intervalId = setInterval(updateTimer, 1000);
    } else {
        clearTimer();
    }
});

// Cleanup on unmount to prevent memory leaks
onUnmounted(() => {
    clearTimer();
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>